import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  CancelCrossIcon,
  Caption1,
  Header3,
  IconButton,
  Modal,
  Paper,
  SlideFromTop,
  Tooltip,
} from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import {
  getCurrencyCodeByProjectId,
  getDefaultCurrency,
  getFormatCurrencyArgs,
} from "~/store/selectors/selector.currencies";
import { CostSummaryJobTypeCostItem } from "./CostSummaryJobTypeCostItem";
import { CostSummaryLoader } from "./CostSummaryLoader";
import { CostSummaryTotalRow } from "./CostSummaryTotalRow";
import { ROW_CLASSES_ITEM, ROW_CLASSES_SHARED } from "./utils";

export const CostSummary = ({
  open,
  onClose,
  loading,
  costByJobType,
  projectId,
  expenses,
  nonBillableExpenses,
  totalBillable,
  totalNonBillable,
  totalInternal,
  withoutInternalRate,
  total,
}) => {
  const { t } = useTranslation();

  const filteredCostByJobType = costByJobType.filter((costData) => costData?.hours !== 0);

  const defaultCurrency = useSelector(getDefaultCurrency);
  const currencyCode = useSelector((state) => getCurrencyCodeByProjectId(state, projectId));
  const currencyArgs = useSelector((state) => getFormatCurrencyArgs(state, currencyCode));

  return (
    <Modal
      open={open}
      onClose={onClose}
    >
      <SlideFromTop in={open}>
        <Paper className="tw-overflow-auto tw-max-h-[80%] tw-max-w-[700px] tw-mx-2 sm:tw-mx-20 md:tw-mx-auto tw-mt-32">
          <div className="tw-w-[700px] tw-h-full">
            <div className="tw-flex tw-justify-between tw-items-center tw-py-5 tw-px-[30px] tw-border-0 tw-border-solid tw-border-b tw-border-border-primary">
              <Header3>{t("Cost Summary")}</Header3>
              <Tooltip title={t("Close")}>
                <IconButton
                  onClick={onClose}
                  variant="text gray"
                >
                  <CancelCrossIcon />
                </IconButton>
              </Tooltip>
            </div>
            <div className="tw-py-5 tw-px-[30px]">
              {loading && <CostSummaryLoader />}
              {!loading && (
                <>
                  {!!filteredCostByJobType.length && (
                    <>
                      <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED)}>
                        <Caption1 weight="bold">{t("Time")}</Caption1>
                        <Caption1
                          weight="bold"
                          className="tw-truncate"
                        >
                          {t("Job Type")}
                        </Caption1>
                        <Caption1
                          weight="bold"
                          align="right"
                        >
                          {t("Internal Cost")}
                        </Caption1>
                        <Caption1
                          weight="bold"
                          align="right"
                        >
                          {t("Spent")}
                        </Caption1>
                      </div>
                      {filteredCostByJobType.map((item, index) => (
                        <CostSummaryJobTypeCostItem
                          key={index}
                          total={item.value}
                          internalCost={item.total_internal_value}
                          decimalSeparator={currencyArgs.decimal_separator}
                          decimalSpaces={currencyArgs.decimal_spaces}
                          thousandSeparator={currencyArgs.thousand_separator}
                          currencyCode={currencyCode}
                          defaultCurrency={defaultCurrency?.code}
                          hours={item.hours}
                          rate={item.rate}
                          jobTypeId={item.id}
                          withoutInternalRate={item.without_internal_rate}
                        />
                      ))}
                      <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED, "tw-h-8")} />
                    </>
                  )}
                  {!!expenses || !!nonBillableExpenses ? (
                    <>
                      <CostSummaryTotalRow
                        value={expenses}
                        label={`+${t("Expenses")}`}
                        decimalSeparator={currencyArgs.decimal_separator}
                        decimalSpaces={currencyArgs.decimal_spaces}
                        thousandSeparator={currencyArgs.thousand_separator}
                        currencyCode={currencyCode}
                      />
                      <CostSummaryTotalRow
                        value={nonBillableExpenses}
                        label={t("Expenses Non-Billable")}
                        decimalSeparator={currencyArgs.decimal_separator}
                        decimalSpaces={currencyArgs.decimal_spaces}
                        thousandSeparator={currencyArgs.thousand_separator}
                        currencyCode={currencyCode}
                      />
                      <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED, "tw-h-8")} />
                    </>
                  ) : null}

                  <CostSummaryTotalRow
                    value={totalBillable}
                    label={t("Total Billable")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={currencyCode}
                  />
                  <CostSummaryTotalRow
                    value={totalNonBillable}
                    label={t("Total Non-Billable")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={currencyCode}
                  />
                  <CostSummaryTotalRow
                    value={total}
                    label={t("Total")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={currencyCode}
                  />
                  <CostSummaryTotalRow
                    value={totalInternal}
                    label={t("Total Internal Cost")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={defaultCurrency?.code}
                    withoutInternalRate={withoutInternalRate}
                  />
                </>
              )}
            </div>
          </div>
        </Paper>
      </SlideFromTop>
    </Modal>
  );
};

CostSummary.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  loading: PropTypes.bool,
  costByJobType: PropTypes.array,
  projectId: PropTypes.number.isRequired,
  expenses: PropTypes.number,
  nonBillableExpenses: PropTypes.number,
  totalBillable: PropTypes.number,
  totalNonBillable: PropTypes.number,
  totalInternal: PropTypes.number,
  withoutInternalRate: PropTypes.bool,
  total: PropTypes.number,
};

CostSummary.defaultProps = {
  costByJobType: [],
};
